/**
 * Busca de emoji por palavra no seletor (igual ao WhatsApp: "coração",
 * "risada", "obrigado").
 *
 * Nomes e palavras-chave em português vêm do CLDR (Unicode), via
 * `emojibase-data/pt/compact.json` (~90 KB gzip). Só é baixado na PRIMEIRA
 * busca — import dinâmico, o Vite separa num arquivo próprio com cache longo —
 * então quem nunca busca não paga nada.
 */

export interface ItemDaBase {
  unicode: string;
  label: string;
  tags?: string[];
}

/** Minúsculas e sem acento: "Coração" → "coracao". */
export function normalizar(texto: string): string {
  return texto.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/** O seletor e a base nem sempre trazem o seletor de variação (U+FE0F). */
const semVariacao = (emoji: string): string => emoji.replace(/\uFE0F/g, '');

/** emoji (sem FE0F) → texto pesquisável (nome + palavras-chave, normalizado). */
export function montarIndice(base: readonly ItemDaBase[]): Map<string, string> {
  const indice = new Map<string, string>();
  for (const item of base) {
    if (!item.unicode || !item.label) continue;
    const texto = normalizar([item.label, ...(item.tags ?? [])].join(' '));
    indice.set(semVariacao(item.unicode), texto);
  }
  return indice;
}

/**
 * Emojis do seletor que batem com o termo: todas as palavras digitadas
 * precisam aparecer (em qualquer ordem). Quem tem o NOME começando pelo termo
 * vem primeiro; depois, a ordem do próprio seletor.
 */
export function buscarEmojis(
  termo: string,
  emojis: readonly string[],
  indice: ReadonlyMap<string, string>,
  limite = 160,
): string[] {
  const palavras = normalizar(termo).split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return [];
  const inicio: string[] = [];
  const resto: string[] = [];
  for (const e of emojis) {
    const texto = indice.get(semVariacao(e));
    if (!texto || !palavras.every((p) => texto.includes(p))) continue;
    (texto.startsWith(palavras[0] as string) ? inicio : resto).push(e);
  }
  return [...inicio, ...resto].slice(0, limite);
}

let carregamento: Promise<Map<string, string>> | null = null;

/** Baixa e monta o índice uma vez só (falhou: tenta de novo na próxima busca). */
export function carregarIndiceDeEmojis(): Promise<Map<string, string>> {
  if (!carregamento) {
    carregamento = import('emojibase-data/pt/compact.json')
      .then((m) => montarIndice((m.default ?? m) as unknown as ItemDaBase[]))
      .catch((err) => {
        carregamento = null;
        throw err;
      });
  }
  return carregamento;
}
